import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import AppShell from "@/components/AppShell";
import { useLang } from "@/contexts/LangContext";
import { api } from "@/lib/api";
import { t } from "@/lib/i18n";
import { Search, Plus, Minus, BookmarkPlus, BookmarkCheck, ArrowRight } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

export default function KnowledgeBase() {
  const { lang } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const initialCat = params.get("cat") || "all";

  const [cat, setCat] = useState(initialCat);
  const [q, setQ] = useState("");
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [bookmarks, setBookmarks] = useState(new Set());
  const [open, setOpen] = useState({});

  useEffect(() => {
    api.get("/categories").then(({ data }) => setCategories(data));
  }, []);

  useEffect(() => {
    const fetch = async () => {
      const { data } = await api.get("/articles", { params: { category: cat, q: q || undefined } });
      setArticles(data);
    };
    fetch();
  }, [cat, q]);

  useEffect(() => {
    if (!user) return;
    api.get("/bookmarks").then(({ data }) => setBookmarks(new Set(data.map((a) => a.article_id)))).catch(() => {});
  }, [user]);

  const tabs = useMemo(() => [{ key: "all", label: t(lang,"kb.all") }, ...categories.map((c) => ({ key: c.key, label: c[lang] }))], [categories, lang]);

  const toggleBookmark = async (article_id) => {
    if (!user) { navigate("/login"); return; }
    if (bookmarks.has(article_id)) {
      await api.delete(`/bookmarks/${article_id}`);
      const n = new Set(bookmarks); n.delete(article_id); setBookmarks(n);
      toast.success("Removed from bookmarks");
    } else {
      await api.post(`/bookmarks`, { article_id });
      const n = new Set(bookmarks); n.add(article_id); setBookmarks(n);
      toast.success("Bookmarked");
    }
  };

  return (
    <AppShell>
      <div className="editorial-rule pt-4">
        <h1 className="font-display text-4xl md:text-5xl">{t(lang,"kb.title")}</h1>
      </div>
      <p className="mt-3 text-[#495057] max-w-2xl">{t(lang,"kb.sub")}</p>

      <div className="mt-8 flex flex-col md:flex-row items-stretch md:items-center gap-3">
        <div className="flex-1 relative">
          <Search className="absolute top-1/2 -translate-y-1/2 start-3 w-4 h-4 text-[#868E96]" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t(lang,"kb.search_ph")}
            data-testid="kb-search-input"
            className="w-full ps-9 pe-4 py-3 border border-[#E9ECEF] bg-white focus:outline-none focus:border-[#0A1128]"
          />
        </div>
      </div>

      <div className="mt-6 flex flex-wrap gap-2">
        {tabs.map((tb) => (
          <button
            key={tb.key}
            data-testid={`kb-tab-${tb.key}`}
            onClick={() => setCat(tb.key)}
            className={`px-4 py-2 text-xs tracking-[0.18em] uppercase border transition-colors ${
              cat === tb.key ? "bg-[#0A1128] text-white border-[#0A1128]" : "border-[#E9ECEF] text-[#495057] hover:bg-white"
            }`}
          >
            {tb.label}
          </button>
        ))}
      </div>

      <div className="mt-8 bg-white border border-[#E9ECEF]" data-testid="kb-articles-list">
        {articles.length === 0 && (
          <div className="p-8 text-[#868E96] text-sm">No articles match your filter.</div>
        )}
        {articles.map((a) => {
          const isOpen = !!open[a.article_id];
          const isBk = bookmarks.has(a.article_id);
          return (
            <div key={a.article_id} className="border-b border-[#E9ECEF] last:border-b-0">
              <button
                className="w-full text-start p-5 flex items-start gap-4 hover:bg-[#F8F9FA] transition-colors"
                onClick={() => setOpen({ ...open, [a.article_id]: !isOpen })}
                data-testid={`kb-article-toggle-${a.article_id}`}
              >
                <div className="mt-1 text-[#C65D47]">
                  {isOpen ? <Minus className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
                </div>
                <div className="flex-1">
                  <div className="text-xs tracking-[0.2em] uppercase text-[#868E96]">{a.law_reference}</div>
                  <div className="font-display text-xl mt-1">{a.question?.[lang] || a.question?.en}</div>
                </div>
              </button>
              {isOpen && (
                <div className="px-5 pb-6 ps-12 max-w-3xl">
                  <p className="text-[#0A1128] leading-relaxed whitespace-pre-wrap text-base" data-testid={`kb-article-answer-${a.article_id}`}>
                    {a.answer?.[lang] || a.answer?.en}
                  </p>
                  <div className="mt-5 flex items-center justify-between">
                    <div className="flex flex-wrap gap-2">
                      {(a.tags || []).map((tg) => (
                        <span key={tg} className="text-xs uppercase tracking-[0.18em] bg-[#F1F3F5] text-[#495057] px-2 py-1">{tg}</span>
                      ))}
                    </div>
                    <div className="flex items-center gap-2">
                      <button onClick={() => toggleBookmark(a.article_id)} className="inline-flex items-center gap-2 text-sm border border-[#E9ECEF] px-3 py-2 hover:bg-[#F8F9FA]" data-testid={`kb-bookmark-${a.article_id}`}>
                        {isBk ? <><BookmarkCheck className="w-4 h-4 text-[#C65D47]" /> {t(lang,"kb.bookmarked")}</> : <><BookmarkPlus className="w-4 h-4" /> {t(lang,"kb.bookmark")}</>}
                      </button>
                      <Link to={`/article/${a.article_id}`} className="inline-flex items-center gap-1 text-sm underline" data-testid={`kb-open-${a.article_id}`}>
                        Open <ArrowRight className="w-3 h-3" />
                      </Link>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </AppShell>
  );
}
